import React, { useEffect, useState } from 'react';
import { Modal, ModalBody, ModalHeader } from 'reactstrap';
import PropTypes from 'prop-types';
import * as Icon from 'react-feather';
import { useDispatch } from 'react-redux';

import CallexLoader from '../../../components/CallexLoader';
import { formatTableDateTime } from '../../../utils/common';
import { botChatReportDetailsGet } from '../../../store/slices/tenantSlices/dailyReportSlice';

const BOT_ROLES = ['assistant', 'bot', 'ai'];

// The API doc only calls the transcript "the full message list", so accept both the
// OpenAI-style { role, content } shape and the CDR transcript's { type, message } shape.
const normalizeTranscript = (transcript) => {
  let list = transcript;
  if (typeof list === 'string') {
    try {
      list = JSON.parse(list);
    } catch {
      list = [];
    }
  }
  if (!Array.isArray(list)) return [];
  return list
    .map((msg) => ({
      role: String(msg?.role ?? msg?.type ?? msg?.sender ?? '').toLowerCase(),
      text: msg?.content ?? msg?.message ?? msg?.text ?? '',
      time: msg?.timestamp ?? msg?.created_at ?? msg?.date_and_time ?? null
    }))
    .filter((msg) => msg.role !== 'system' && msg.text);
};

const AiChatTranscriptModal = ({ isOpen, onClose, chatId }) => {
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(true);
  const [messages, setMessages] = useState([]);

  const fetchTranscript = async () => {
    setIsLoading(true);
    try {
      const response = await dispatch(botChatReportDetailsGet(chatId)).unwrap();
      setMessages(normalizeTranscript(response?.data?.transcript));
    } catch (error) {
      console.log('error', error);
      setMessages([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen && chatId) {
      fetchTranscript();
    }
    if (!isOpen) {
      setMessages([]);
    }
    // eslint-disable-next-line
  }, [isOpen, chatId]);

  return (
    <Modal isOpen={isOpen} toggle={onClose} size="xl" centered>
      <ModalHeader toggle={onClose}>Chat Transcript</ModalHeader>
      <ModalBody className="call-details-modal">
        {isLoading ? (
          <div
            className="d-flex justify-content-center align-items-center"
            style={{ height: '40vh' }}>
            <CallexLoader />
          </div>
        ) : messages.length ? (
          <div className="chat-wrapper">
            {messages.map((msg, index) => {
              const bot = BOT_ROLES.includes(msg.role);
              return (
                <div key={index} className={`chat-row ${bot ? 'assistant' : 'user'}`}>
                  {bot && (
                    <div className="avatar assistant">
                      <Icon.Cpu size={16} />
                    </div>
                  )}
                  <div className="message-group">
                    <div className="chat-bubble" style={{ whiteSpace: 'pre-wrap' }}>
                      {msg.text}
                    </div>
                    {msg.time && <div className="chat-time">{formatTableDateTime(msg.time)}</div>}
                  </div>
                  {!bot && (
                    <div className="avatar user">
                      <Icon.User size={16} />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <div className="p-3 text-center">No transcript available</div>
        )}
      </ModalBody>
    </Modal>
  );
};

AiChatTranscriptModal.propTypes = {
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  chatId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
};

export default AiChatTranscriptModal;
